import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Loader2 } from 'lucide-react';
import { listStudentPublishedIbooks, type StudentIbookRecord } from '@ibook/api/ibook.client';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { ROUTES } from '../../../utils/constants';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { findMostRecentlyOpenedBook, progressActionLabel } from '../../ibook/reader/studentProgress';

export function IbookDashboardCard() {
  const { isSchoolFeatureEnabled, loading: featureLoading } = useFeatureAccess();
  const [books, setBooks] = useState<StudentIbookRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const items = await listStudentPublishedIbooks({ limit: 100 });
      setBooks(items);
    } catch {
      setBooks([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (featureLoading) return;
    if (!isSchoolFeatureEnabled('ibook_reader_enabled')) {
      setLoading(false);
      return;
    }
    void load();
  }, [featureLoading, isSchoolFeatureEnabled, load]);

  const recentBook = useMemo(() => findMostRecentlyOpenedBook(books), [books]);

  if (featureLoading || !isSchoolFeatureEnabled('ibook_reader_enabled')) {
    return null;
  }

  return (
    <Card className="border-purple-200/60 dark:border-purple-500/25">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <BookOpen className="h-5 w-5 text-app-primary" aria-hidden />
          iBooks
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading your library…
          </p>
        ) : error ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Could not load iBooks right now.
          </p>
        ) : books.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No iBooks have been published for your school and grade yet.
          </p>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {books.length} published {books.length === 1 ? 'book' : 'books'} available for you.
            </p>
            {recentBook ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900/40">
                <p className="text-xs uppercase tracking-wide text-gray-500">Continue reading</p>
                <p className="mt-1 font-medium text-gray-900 dark:text-white">{recentBook.title}</p>
                <Link
                  to={ROUTES.STUDENT_IBOOK_READER.replace(':bookId', recentBook.book_id)}
                  className="mt-2 inline-flex text-sm font-medium text-app-primary hover:underline"
                >
                  {progressActionLabel(recentBook.book_id, recentBook.page_count)}
                </Link>
              </div>
            ) : null}
          </>
        )}
        <Link
          to={ROUTES.STUDENT_IBOOKS}
          className="inline-flex items-center justify-center rounded-lg bg-app-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          Open iBook Library
        </Link>
      </CardContent>
    </Card>
  );
}

export default IbookDashboardCard;
